import { useEffect, useMemo, useState, type JSX } from 'react';
import { Pencil2Icon } from '@radix-ui/react-icons';
import clsx from 'clsx';
import { z } from 'zod';
import { PillButton } from '@/components/entity/PillButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import { usePropertyMarketingSummary } from '@/hooks/useDashboardProperties';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type { PropertySummary } from '@/lib/types/property';
import { formatPropertyAddress } from '@/lib/utils/propertyFormatting';
import { useFormController } from '@/lib/forms/formController';
import {
  PropertyActionIconButton,
  propertyActionIconClassName,
} from '@/components/dashboard/properties/PropertyActionIconButton';
import { Drawer } from '@/components/layout/Drawer';

const MARKETING_SUMMARY_FIELD_SCHEMA = z
  .string()
  .trim()
  .min(20, 'Marketing summary must be at least 20 characters long')
  .max(2000, 'Marketing summary must be 2,000 characters or fewer');
const MARKETING_SUMMARY_SCHEMA = z.object({
  marketingSummary: MARKETING_SUMMARY_FIELD_SCHEMA,
});

type ButtonVariant = 'primary' | 'secondary';
type ButtonSize = 'sm' | 'md';

interface PropertyMarketingSummaryDrawerProps {
  property: PropertySummary;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  className?: string;
  mode?: 'text' | 'icon';
}

export function PropertyMarketingSummaryDrawer({
  property,
  variant = 'secondary',
  size = 'md',
  fullWidth = false,
  className,
  mode = 'text',
}: PropertyMarketingSummaryDrawerProps): JSX.Element {
  const user = useDashboardUser();
  const mutation = usePropertyMarketingSummary({ user });
  const [open, setOpen] = useState(false);
  const [lastSavedValue, setLastSavedValue] = useState(property.marketingSummary);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const form = useFormController({
    initialValues: { marketingSummary: property.marketingSummary },
    schema: MARKETING_SUMMARY_SCHEMA,
    onSubmit: async (values) => {
      await mutation.mutateAsync({
        property,
        marketingSummary: values.marketingSummary,
      });
    },
    onSuccess: (values) => {
      setLastSavedValue(values.marketingSummary);
      setLastSavedAt(new Date().toISOString());
    },
    onError: () => {
      // handled via submitError
    },
    mapSubmitError: (error) => ({
      message: error instanceof Error ? error.message : 'Unable to save changes.',
    }),
  });
  const debouncedValue = useDebouncedValue(form.values.marketingSummary, 800);
  const resetForm = form.reset;
  const setFormErrors = form.setErrors;
  const submitForm = form.handleSubmit;
  const isFormSubmitting = form.isSubmitting;

  useEffect(() => {
    const latest = property.marketingSummary;
    if (!open) {
      resetForm({ marketingSummary: latest });
      setLastSavedValue(latest);
      setLastSavedAt(null);
      return;
    }
  }, [open, property.marketingSummary, resetForm]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const normalizedValue = normalizeWhitespace(debouncedValue);
    const normalizedSaved = normalizeWhitespace(lastSavedValue);
    if (normalizedValue === normalizedSaved) {
      return;
    }
    const parsed = MARKETING_SUMMARY_FIELD_SCHEMA.safeParse(debouncedValue);
    if (!parsed.success) {
      const issue = parsed.error.issues.at(0);
      const message = issue?.message ?? 'Marketing summary is invalid.';
      setFormErrors({ marketingSummary: message });
      return;
    }
    setFormErrors({});
    if (mutation.isPending || isFormSubmitting) {
      return;
    }
    void submitForm();
  }, [
    debouncedValue,
    isFormSubmitting,
    lastSavedValue,
    mutation.isPending,
    open,
    setFormErrors,
    submitForm,
  ]);

  const statusLabel = useMemo(() => {
    if (form.isSubmitting || mutation.isPending) {
      return 'Saving…';
    }
    const fieldError = form.errors.marketingSummary;
    const submitError = form.submitError?.trim();
    const hasFieldError = typeof fieldError === 'string' && fieldError.length > 0;
    const hasSubmitError = typeof submitError === 'string' && submitError.length > 0;
    if (hasSubmitError || hasFieldError) {
      return fieldError ?? submitError ?? 'Unable to save changes';
    }
    if (form.submitSuccess && lastSavedAt !== null) {
      return `Saved at ${formatTime(lastSavedAt)}`;
    }
    if (form.isDirty) {
      return 'Unsaved changes';
    }
    return 'Up to date';
  }, [
    form.errors.marketingSummary,
    form.isDirty,
    form.isSubmitting,
    form.submitError,
    form.submitSuccess,
    lastSavedAt,
    mutation.isPending,
  ]);

  const statusTone = useMemo(() => {
    if (form.isSubmitting || mutation.isPending) {
      return 'text-brand-primary';
    }
    const fieldError = form.errors.marketingSummary;
    const submitError = form.submitError?.trim();
    const hasFieldError = typeof fieldError === 'string' && fieldError.length > 0;
    const hasSubmitError = typeof submitError === 'string' && submitError.length > 0;
    if (hasSubmitError || hasFieldError) {
      return 'text-red-600';
    }
    if (form.submitSuccess) {
      return 'text-green-600';
    }
    if (form.isDirty) {
      return 'text-amber-600';
    }
    return 'text-text-primary/70';
  }, [
    form.errors.marketingSummary,
    form.isDirty,
    form.isSubmitting,
    form.submitError,
    form.submitSuccess,
    mutation.isPending,
  ]);

  const marketingSummaryValue =
    typeof form.values.marketingSummary === 'string' ? form.values.marketingSummary : '';
  const charCount = marketingSummaryValue.length;
  const hasFieldError = typeof form.errors.marketingSummary === 'string';
  const hasSubmitError = typeof form.submitError === 'string' && form.submitError.length > 0;
  const hasError = hasFieldError || hasSubmitError;
  const fieldState = form.field('marketingSummary');

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          form.reset({ marketingSummary: property.marketingSummary });
          mutation.reset();
          setLastSavedValue(property.marketingSummary);
          setLastSavedAt(null);
        }
        setOpen(next);
      }}
      title="Marketing summary"
      description={formatPropertyAddress(property.address)}
      trigger={
        mode === 'icon' ? (
          <PropertyActionIconButton label="Edit marketing summary">
            <button
              type="button"
              aria-label="Edit marketing summary"
              className={clsx(propertyActionIconClassName, className)}
              onClick={() => {
                form.setErrors({});
                mutation.reset();
                setOpen(true);
              }}
            >
              <Pencil2Icon className="h-4 w-4" />
              <span className="sr-only">Edit marketing summary</span>
            </button>
          </PropertyActionIconButton>
        ) : (
          <PillButton
            type="button"
            variant={variant}
            size={size}
            className={clsx(fullWidth && 'w-full', className)}
            onClick={() => {
              form.setErrors({});
              mutation.reset();
              setOpen(true);
            }}
          >
            Edit summary
          </PillButton>
        )
      }
      headerContent={
        <span className={clsx('text-xs font-semibold', statusTone)}>{statusLabel}</span>
      }
    >
      <p className="text-sm text-text-primary/80">
        Keep partner-facing messaging current. This summary feeds the admin cards, partner emails,
        and the public property hero copy.
      </p>
      <label className="flex flex-1 flex-col gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          Marketing summary
        </span>
        <textarea
          value={fieldState.value}
          onChange={fieldState.onChange}
          onBlur={fieldState.onBlur}
          maxLength={2000}
          rows={8}
          className={clsx(
            'w-full flex-1 rounded-2xl border px-4 py-3 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:text-sm',
            hasError ? 'border-red-500' : 'border-surface-subtle',
          )}
        />
        <div className="flex flex-wrap items-center justify-between text-xs text-text-primary/60">
          <span className={statusTone}>{statusLabel}</span>
          <span>{charCount}/2000</span>
        </div>
        {hasError ? (
          <p className="text-xs text-red-600">
            {form.errors.marketingSummary ?? form.submitError ?? 'Unable to save changes.'}
          </p>
        ) : null}
      </label>
      <p className="text-xs text-text-primary/60">
        Autosaves after you stop typing. Changes update dashboard cards and public copy immediately
        across desktop, tablet, and mobile layouts.
      </p>
    </Drawer>
  );
}

function normalizeWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function formatTime(timestamp: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(timestamp));
  } catch (error) {
    void error;
    return 'just now';
  }
}
